import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from 'contexts/AuthContext';
import { LangProvider } from 'i18n/LangContext';
import Switcher from 'components/Swicher';
import QuizController from 'components/QuizController';
import ThankPage from 'components/ThankPage';
import SubscriptionSuccess from 'components/SubscriptionSuccess';
import HomePage from 'components/HomePage';
import PricingPage from 'components/PricingPage';
import TermsPage from 'components/TermsPage';
import PrivacyPage from 'components/PrivacyPage';
import RefundPage from 'components/RefundPage';
import LoginPage from 'components/LoginPage';
import DashboardPage from 'components/DashboardPage';
import ProtectedRoute from 'components/ProtectedRoute';
import AboutPage from 'components/AboutPage';
import FAQsPage from 'components/FAQsPage';
import ContactPage from 'components/ContactPage';
import CookieConsent from 'components/CookieConsent';
import RefRedirect from 'components/RefRedirect';
import ReferralsPage from 'components/ReferralsPage';
import StatePage from 'app/StatePage';
import DirectionsPage from 'app/DirectionsPage';
import DirectionPage from 'app/DirectionPage';
import { PatternsPage, PatternPage } from 'app/PatternsPage';
import InsightsPage from 'app/InsightsPage';
import TasksPage from 'app/TasksPage';
import TaskPage from 'app/TaskPage';
import TaskDayPage from 'app/TaskDayPage';
import AccountPage from 'app/AccountPage';
import amplitude from 'amplitude-js';
import { captureRef, readRefParam, shouldLogVisitToday } from 'utils/referral';
import trackEvent from 'utils/trackEvent';

const App = () => {
    useEffect(() => {
        const userId = localStorage.getItem('userId');
        const amplitudeApiKey = process.env.REACT_APP_AMPLITUDE_API_KEY || '3207d4da7e428772eae0e7a063b5ae62';
        // Always initialise Amplitude (even for anonymous visitors) — otherwise
        // logEvent fires on an unconfigured instance with no API key and Amplitude
        // returns 400, silently dropping every anonymous funnel event. The userId
        // is attached only once we actually have one.
        if (amplitudeApiKey) {
            amplitude.getInstance().init(amplitudeApiKey, userId || undefined, {
                serverZone: 'EU',
                apiEndpoint: 'api.eu.amplitude.com',
            });
            if (userId) amplitude.getInstance().setUserId(userId);
        }

        // Referral entry via ?ref= on any landing URL: log a visit (once/day per
        // code) and capture first-touch attribution (+ one-time ref_click).
        const ref = readRefParam(window.location.search);
        if (ref && shouldLogVisitToday(ref)) trackEvent('ref_visit', { refCode: ref });
        const newlySet = captureRef(ref);
        if (newlySet) trackEvent('ref_click', { refCode: newlySet });
    }, []);

    return (
        <div className="App">
            <LangProvider>
                <AuthProvider>
                    <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/pricing" element={<PricingPage />} />

                        {/* Referral link entry + partner dashboard */}
                        <Route path="/r/:code" element={<RefRedirect />} />
                        <Route path="/referrals" element={<ReferralsPage />} />
                        
                        {/* The app surface lives at /dashboard because four flows
                            already land there — login, quiz submit, the extended
                            track flow and the email step. Swapping what renders at
                            that URL beats adding a new one and chasing redirects.

                            Built tabs: Стан and Напрями. The remaining three render
                            an honest "not yet" screen so the five-tab bar stays
                            truthful instead of linking into nothing.

                            The previous editorial dashboard is kept at
                            /dashboard/legacy — comparable side by side, and a
                            rollback is this route swap rather than a revert.

                            All of it keeps the original gate: login + payment. */}
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute requirePayment>
                                    <StatePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/directions"
                            element={
                                <ProtectedRoute requirePayment>
                                    <DirectionsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/directions/:code"
                            element={
                                <ProtectedRoute requirePayment>
                                    <DirectionPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/patterns"
                            element={
                                <ProtectedRoute requirePayment>
                                    <PatternsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/patterns/:code"
                            element={
                                <ProtectedRoute requirePayment>
                                    <PatternPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/insights"
                            element={
                                <ProtectedRoute requirePayment>
                                    <InsightsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/tasks"
                            element={
                                <ProtectedRoute requirePayment>
                                    <TasksPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/tasks/:code"
                            element={
                                <ProtectedRoute requirePayment>
                                    <TaskPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/tasks/:code/day"
                            element={
                                <ProtectedRoute requirePayment>
                                    <TaskDayPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/account"
                            element={
                                <ProtectedRoute requirePayment>
                                    <AccountPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/legacy"
                            element={
                                <ProtectedRoute requirePayment>
                                    <DashboardPage />
                                </ProtectedRoute>
                            }
                        />
                        {/* Anything bookmarked under /app during development. */}
                        <Route path="/app/*" element={<Navigate to="/dashboard" replace />} />

                        {/* Quiz - New quiz system with pages[] format. The
                            optional :step segment gives each step its own URL
                            (client-side only) so funnels can chart drop-off. */}
                        <Route path="/quiz/:quizId" element={<QuizController />} />
                        <Route path="/quiz/:quizId/:step" element={<QuizController />} />

                        {/* Topping up a direction after payment. Its own namespace
                            so an existing customer's steps stay out of the
                            acquisition funnel's GA paths. Paid users only — the
                            flow skips email and checkout entirely. */}
                        <Route
                            path="/quiz-extended/:quizId/:track"
                            element={
                                <ProtectedRoute requirePayment>
                                    <QuizController />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/quiz-extended/:quizId/:track/:step"
                            element={
                                <ProtectedRoute requirePayment>
                                    <QuizController />
                                </ProtectedRoute>
                            }
                        />

                        {/* Quiz V1 - Legacy quiz system with screens[] format */}
                        <Route path="/quiz-v1/:quizId" element={<Switcher />} />
                        
                        {/* Thank you page - PUBLIC to allow auto-login after payment */}
                        <Route path="/thank-page/:quizId" element={<ThankPage />} />
                        <Route path="/subscription-success/:quizId" element={<SubscriptionSuccess />} />
                        
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/refund" element={<RefundPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/faqs" element={<FAQsPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                    </Routes>
                    <CookieConsent />
                </BrowserRouter>
                </AuthProvider>
            </LangProvider>
        </div>
    );
};

export default App;
